import { FC } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import FirstLevelTable from './components/FirstLevelTable';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import {
  SupplementaryRulesBackendFirstTableConfig,
  SupplementaryRulesBackendResponse,
  SupplementaryRulesBackendSecondTableConfig,
  SupplementaryRulesFirstTableConfig,
  SupplementaryRulesPosition,
  SupplementaryRulesSecondTableConfig,
} from './supplementaryRules.types';
import { AxiosInstance } from '../../../../utilities/requestClient';
import { makeUniqueDataKey } from '../../../../utilities/urlBuilder';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
  toTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import { categoryProviderByNumber } from '../../../../utilities/categoryProvider';
import { activeSectionSelector } from '../../../../redux/pages/selectors';


function useSupplementaryRules(
  fundId?: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<SupplementaryRulesBackendResponse, AxiosError>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<SupplementaryRulesBackendResponse, AxiosError>({
    queryKey: [
      makeUniqueDataKey(fundId || '', 'supplementaryRules', positionDate),
      fundId || '',
      positionDate || '',
    ],
    queryFn: async () => {

      const url = 'additional_compliance/' + fundId + '/' + positionDate;
      return (await AxiosInstance.get(url)).data;
    },
    enabled: !!fundId && !!positionDate,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

function buildFirstTableConfigFromSchema(
  section: string,
  schema: SupplementaryRulesBackendFirstTableConfig,
): SupplementaryRulesFirstTableConfig {
  return {
    noteSubject: `${section}_supplementary-rules`,
    noteTopicPrefix: 'rule_',
    columns: schema.columns.map((col) => {
      if (col.column_key === '#') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'row_number',
        };
      }

      if (col.column_key === 'status') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'status',
        };
      }

      if (col.column_key === 'notes') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'notes',
        };
      }

      return {
        column_header: col.column_name,
        column_key: col.column_key,
        type: 'text',
      };
    }),
  };
}

function buildSecondTableConfigFromSchema(
  section: string,
  schema: SupplementaryRulesBackendSecondTableConfig,
): SupplementaryRulesSecondTableConfig {
  return {
    parentMatchField: schema.parentMatchField,
    childMatchField: schema.childMatchField,
    noteSubject: `${section}_supplementary-rules`,
    columns: schema.columns.map((col) => {
      if (col.column_key === '#' || col.column_key === 'number') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'row_number',
        };
      }

      if (col.column_key === 'gm_id') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            categoryProviderByNumber(position.gm_id)[1],
        };
      }

      if (col.column_key === 'raw_position') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            addCommasToNumbersAndRound(position.raw_position),
        };
      }

      if (col.column_key === 'market_price') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) => toTwoDecimalPlaces(position.market_price),
        };
      }

      if (col.column_key === 'risk_metric_value') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            addCommasToNumbersAndRound(position.risk_metric_value),
        };
      }

      if (col.column_key === 'exposure') {
        return {
          column_header: col.column_name,
          column_key: col.column_key,
          type: 'text',
          formatter: (position: SupplementaryRulesPosition) =>
            percentageToTwoDecimalPlaces(position.exposure),
        };
      }

      return {
        column_header: col.column_name,
        column_key: col.column_key,
        type: 'text',
        sourceKey: col.column_key,
      };
    }),
  };
}

const SupplementaryRulesPage: FC<FundInfoComponentProps> = ({
  fundId,
  fundName,
  positionDate,
}) => {
  const supplementaryRulesQuery = useSupplementaryRules(fundId, positionDate);
  const isPositionDateLoading = !!fundId && !positionDate;
  const section = useSelector(activeSectionSelector) || '';

  return (
    <QueryBoundary
      isLoading={isPositionDateLoading || supplementaryRulesQuery.isLoading}
      isFetching={supplementaryRulesQuery.isFetching}
      isError={supplementaryRulesQuery.isError}
      data={
        supplementaryRulesQuery.data?.limit_status?.length &&
          supplementaryRulesQuery.data.schema?.firstTable &&
          supplementaryRulesQuery.data.schema?.secondTable
          ? supplementaryRulesQuery.data
          : null
      }
      loadingMessages={
        isPositionDateLoading
          ? ['Loading position date...']
          : ['Loading Supplementary Rules Data']
      }
      noDataMessage="No supplementary rules data available."
      errorMessage="Unable to load supplementary rules data."
    >
      {(data) => {
        const firstTableConfig = buildFirstTableConfigFromSchema(
          section,
          data.schema.firstTable,
        );
        const secondTableConfig = buildSecondTableConfigFromSchema(
          section,
          data.schema.secondTable,
        );

        return (
          <FirstLevelTable
            fundDetails={{ id: fundId, name: fundName }}
            supplementaryRulesData={data}
            firstTable={firstTableConfig}
            secondTable={secondTableConfig}
          />
        );
      }}
    </QueryBoundary>
  );
};

export default SupplementaryRulesPage;
